import React, { useCallback, useEffect, useState } from "react";
import { Button, ControlLabel, FormControl, FormGroup, Grid } from "react-bootstrap";
import { createCallType, fetchCallTypes, updateCallType } from "./callWorkspaceApi";
import { getToken, getStoredUser, isSuperAdmin } from "./callWorkspace.utils";
import { getApiErrorMessage } from "utils/getApiErrorMessage";
import "./callWorkspace.css";

const emptyForm = () => ({
  name: "",
  description: "",
  default_priority: "normal",
  respects_blacklist: true,
  is_active: true,
});

const CallTypesContainer = ({ history }) => {
  const [types, setTypes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return fetchCallTypes(getToken())
      .then((response) => setTypes(response.data || []))
      .catch((apiError) => setError(getApiErrorMessage(apiError)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = "Call Types | Call Workspace";
    load();
  }, [load]);

  if (!isSuperAdmin(getStoredUser())) {
    return <main className="cw-page"><Grid fluid><div className="cw-alert cw-alert-error">Only a Super Admin can manage call types.</div></Grid></main>;
  }

  const startEdit = (type) => {
    setEditingId(type.id);
    setForm({
      name: type.name,
      description: type.description || "",
      default_priority: type.default_priority || "normal",
      respects_blacklist: !!type.respects_blacklist,
      is_active: !!type.is_active,
    });
    setError("");
    setMessage("");
  };

  const submit = (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    setMessage("");
    const payload = { ...form };
    const request = editingId
      ? updateCallType(getToken(), editingId, payload)
      : createCallType(getToken(), payload);
    request
      .then((response) => {
        setMessage(response.message || (editingId ? "Call type updated." : "Call type created."));
        setForm(emptyForm());
        setEditingId(null);
        return load();
      })
      .catch((apiError) => setError(getApiErrorMessage(apiError)))
      .finally(() => setSaving(false));
  };

  return (
    <main className="cw-page">
      <Grid fluid>
        <div className="cw-header">
          <div>
            <button type="button" className="cw-back-link" onClick={() => history.push("/admin/call-workspace/batch-management")}>← Back to Batch Management</button>
            <p className="cw-kicker">CALL OPERATIONS</p>
            <h1>Call types</h1>
            <p>Create and manage the reasons a call task enters the workspace.</p>
          </div>
        </div>
        {message ? <div className="cw-alert cw-alert-success">{message}</div> : null}
        {error ? <div className="cw-alert cw-alert-error" role="alert">{error}</div> : null}
        <section className="cw-panel">
          <h2>{editingId ? "Edit call type" : "Create call type"}</h2>
          <form onSubmit={submit}>
            <FormGroup>
              <ControlLabel>Name</ControlLabel>
              <FormControl name="name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required maxLength={150} />
            </FormGroup>
            <FormGroup>
              <ControlLabel>Description</ControlLabel>
              <FormControl componentClass="textarea" name="description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            </FormGroup>
            <FormGroup>
              <ControlLabel>Default priority</ControlLabel>
              <FormControl componentClass="select" name="default_priority" value={form.default_priority} onChange={(event) => setForm({ ...form, default_priority: event.target.value })}>
                {["low", "normal", "high", "urgent"].map((priority) => <option key={priority} value={priority}>{priority}</option>)}
              </FormControl>
            </FormGroup>
            <FormGroup>
              <label><input type="checkbox" checked={form.respects_blacklist} onChange={(event) => setForm({ ...form, respects_blacklist: event.target.checked })} /> Respect “Don’t Call” status</label>
            </FormGroup>
            {editingId ? <FormGroup><label><input type="checkbox" checked={form.is_active} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} /> Active</label></FormGroup> : null}
            <Button type="submit" bsStyle="primary" disabled={saving}>{saving ? "Saving…" : editingId ? "Save changes" : "Create call type"}</Button>{" "}
            {editingId ? <Button type="button" disabled={saving} onClick={() => { setEditingId(null); setForm(emptyForm()); }}>Cancel</Button> : null}
          </form>
        </section>
        <section className="cw-panel cw-table-panel">
          <div className="cw-section-heading"><div><h2>Configured call types</h2></div><Button type="button" disabled={loading} onClick={load}>Refresh</Button></div>
          <div className="cw-table-wrap"><table className="cw-table">
            <thead><tr><th>Code</th><th>Name</th><th>Priority</th><th>Blacklist</th><th>State</th><th>Action</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan="6" className="cw-table-state">Loading call types…</td></tr> : types.length ? types.map((type) => (
                <tr key={type.id}><td><code>{type.code}</code></td><td>{type.name}</td><td>{type.default_priority}</td><td>{type.respects_blacklist ? "Respects Don’t Call" : "May bypass Don’t Call"}</td><td>{type.is_active ? "Active" : "Inactive"}</td><td><Button type="button" onClick={() => startEdit(type)}>Edit</Button></td></tr>
              )) : <tr><td colSpan="6" className="cw-table-state"><strong>No call types configured</strong><span>Create a type above before uploading or generating call tasks.</span></td></tr>}
            </tbody>
          </table></div>
        </section>
      </Grid>
    </main>
  );
};

export default CallTypesContainer;
